import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, LoadScript, Polyline, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { decode } from '@googlemaps/polyline-codec';
import useTranslationCheck from '../utils/UseTranslationCheck'; 
import '../styles/Cotizar.css'; 

const containerStyle = { width: '100%', height: '100vh' }; 
const center = { lat: 18.1419, lng: -94.4114 }; 
const TARIFA_KM_FALLBACK = 50; 
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5257';

/**
 * COMPONENTE MODAL INTERNO
 */
function Modal({ open, onClose, titleId = 'modal-title', children }) {
  const backdropRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setTimeout(() => closeBtnRef.current?.focus(), 0);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={backdropRef}
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose?.();
      }}
    >
      <div className="modal-card" role="document">
        <button
          className="modal-close"
          aria-label="Cerrar"
          onClick={onClose}
          ref={closeBtnRef}
        >
          ✕
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

/**
 * COMPONENTE PRINCIPAL: COTIZAR
 */
const Cotizar = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const navigate = useNavigate();

  // --- HOOKS DE TRADUCCIÓN ---
  const isTranslated = useTranslationCheck('es'); 
  const [dolarDb, setDolarDb] = useState(null);

  // --- ESTADOS DE DATOS ---
  const [ubicaciones, setUbicaciones] = useState([]);
  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [carga, setCarga] = useState('');

  // --- ESTADOS VISUALES ---
  const [routeInfo, setRouteInfo] = useState(null);      
  const [polylinePath, setPolylinePath] = useState([]);  
  const [origenCoords, setOrigenCoords] = useState(null);
  const [destinoCoords, setDestinoCoords] = useState(null);
  
  // --- ESTADOS FERRO ---
  const [ferroPath, setFerroPath] = useState([]); 
  const [ferroInfo, setFerroInfo] = useState(null); 

  // --- ESTADOS DE COSTOS ---
  const [costoInfo, setCostoInfo] = useState(null);       
  const [ferroCostoInfo, setFerroCostoInfo] = useState(null); 

  // --- ESTADOS DE ERROR ---
  const [showSameLocationError, setShowSameLocationError] = useState(false);

  // --- INPUTS DINÁMICOS ---
  const [vehiculosCantidad, setVehiculosCantidad] = useState(1);
  const [petroleoLitros, setPetroleoLitros] = useState('');
  const [fluidosLitros, setFluidosLitros] = useState('');
  const [contenTam, setContenTam] = useState('20'); 
  const [contenTipo, setContenTipo] = useState('full'); 
  const [granelAgriTon, setGranelAgriTon] = useState('');
  const [granelMinTon, setGranelMinTon] = useState('');
  const [generalTon, setGeneralTon] = useState('');
  const [otrosUnidad, setOtrosUnidad] = useState('');
  const [otrosCantidad, setOtrosCantidad] = useState('');

  useEffect(() => {
    setVehiculosCantidad(1);
    setPetroleoLitros('');
    setFluidosLitros('');
    setContenTam('20');
    setContenTipo('full');
    setGranelAgriTon('');
    setGranelMinTon('');
    setGeneralTon('');
    setOtrosUnidad('');
    setOtrosCantidad('');
  }, [carga]);

  // --- 1. LÓGICA DE MONEDA ---

  useEffect(() => {
    const fetchTipoCambio = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Dolar`);
        if (response.ok) {
          const result = await response.json();
          const valString = result.valor || result.Valor || result.data?.valor;
          if (valString) {
            const valNumerico = parseFloat(valString);
            if (!isNaN(valNumerico)) {
              setDolarDb(valNumerico);
            }
          }
        }
      } catch (error) {
        console.error("Error obteniendo tipo de cambio:", error);
      }
    };

    if (isTranslated) {
      fetchTipoCambio();
    }
  }, [isTranslated]);

  const TIPO_CAMBIO = dolarDb || 18.50;

  const formatCurrency = (mxn) => {
    if (mxn === undefined || mxn === null) return '$0.00';
    
    if (isTranslated) {
      return (mxn / TIPO_CAMBIO).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }

    return mxn.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
    });
  };

  // --- 2. UTILIDADES DE CARGA ---

  const toNumber = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };
  
  const toNum = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const s = v.replace(',', '.').replace(/[^\d.-]/g, '');
      const n = Number(s);
      return Number.isFinite(n) ? n : NaN;
    }
    return NaN;
  };

  const getProp = (obj, ...names) => {
    if (!obj) return undefined;
    for (const n of names) {
      if (obj[n] !== undefined) return obj[n];
    }
    return undefined;
  };

  const mapTipoCarga = (key) => {
    switch (key) {
      case 'conten':    return 'Carga contenerizada';
      case 'agricola':  return 'Graneles agrícolas';
      case 'mineral':   return 'Graneles minerales';
      case 'general':   return 'Carga general';
      case 'petroleo':  return 'Petróleo y sus derivados';
      case 'fluidos':   return 'Fluidos';
      case 'vehiculos': return 'Vehículos';
      case 'otros':     return 'Otros';
      default:          return '';
    }
  };

  const deriveCantidad = () => {
    const DENSIDAD_PETROLEO_TON_POR_L = 0.00085; 
    const DENSIDAD_FLUIDO_TON_POR_L   = 0.001;  

    switch (carga) {
      case 'conten':    return 1; 
      case 'vehiculos': return Math.max(1, toNumber(vehiculosCantidad, 1));
      case 'petroleo':  return Math.max(0, toNumber(petroleoLitros) * DENSIDAD_PETROLEO_TON_POR_L);
      case 'fluidos':   return Math.max(0, toNumber(fluidosLitros) * DENSIDAD_FLUIDO_TON_POR_L);
      case 'agricola':  return Math.max(0, toNumber(granelAgriTon));
      case 'mineral':   return Math.max(0, toNumber(granelMinTon));
      case 'general':   return Math.max(0, toNumber(generalTon));
      case 'otros':     return Math.max(0, toNumber(otrosCantidad));
      default:          return 0;
    }
  };

  // --- 3. CARGA DE UBICACIONES ---

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/puerto`)
      .then(res => res.json())
      .then(data => setUbicaciones(data))
      .catch(err => console.error('Error al cargar ubicaciones:', err));
  }, []);

  // --- 4. MAPA ---

  const railBaseOptions = {
    strokeColor: '#1F2937', strokeOpacity: 1, strokeWeight: 4, geodesic: true, clickable: false, zIndex: 1
  };
  const railSleepersOptions = {
    strokeOpacity: 0, strokeWeight: 0, zIndex: 2, clickable: false,
    icons: [{
      icon: { path: 'M 0,-1 0,1', scale: 8, strokeOpacity: 1, strokeWeight: 2, strokeColor: '#FFFFFF' },
      offset: '0', repeat: '28px'
    }]
  };

  const limpiarNombrePuerto = (n) =>
    (n || '').replace(/^puerto\s+de\s+/i, '').replace(/^ciudad\s+de\s+/i, '').replace(/\s+/g, ' ').trim();

  // --- 5. CÁLCULO DE PRECIOS ---

  const calcularCostoFallback = (distanciaKm, tipoCargaLegible) => {
    const total = (Number(distanciaKm) || 0) * TARIFA_KM_FALLBACK;
    return {
      total,
      detalle: `Tarifa base estimativa (${tipoCargaLegible || 'sin tipo'})`
    };
  };

  const llamarApiPrecio = async ({ origenId, destinoId, distanciaKm }) => {
    const tipoCargaLegible = mapTipoCarga(carga);
    if (!tipoCargaLegible) return calcularCostoFallback(distanciaKm);

    const cantidad = deriveCantidad();
    const url = `${API_BASE_URL}/api/CalcularRuta/carretera/cotizar`;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          OrigenId: origenId,
          DestinoId: destinoId,
          TipoCarga: tipoCargaLegible,
          Cantidad: cantidad
        })
      });

      const texto = await resp.text();
      if (!resp.ok) {
        return calcularCostoFallback(distanciaKm, tipoCargaLegible);
      }

      let data;
      try { data = JSON.parse(texto); } catch { data = {}; }

      if (typeof data.precio === 'number') {
        const notaLimpia = (data.nota || `Tipo: ${tipoCargaLegible}`)
          .replace(/(?:\s*\|\s*)?Km\s*(?:ida|fact)\s*:\s*[\d.,]+/gi, '')
          .trim()
          .replace(/\s*\|\s*\|\s*/g, ' | ');

        return {
          total: data.precio,
          detalle: notaLimpia,
          fuente: 'Carretera'
        };
      }
      return calcularCostoFallback(distanciaKm, tipoCargaLegible);
    } catch (e) {
      console.error('Error llamando API de precio:', e);
      return calcularCostoFallback(distanciaKm, tipoCargaLegible);
    }
  };

  const llamarApiPrecioFerro = async ({ origenId, destinoId }) => {
    const tipoCargaLegible = mapTipoCarga(carga);
    if (!tipoCargaLegible) return null;

    const cantidad = deriveCantidad();
    const url = `${API_BASE_URL}/api/CalcularRuta/calcular/precio-ferroviario`;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          OrigenId: origenId,
          DestinoId: destinoId,
          TipoCarga: tipoCargaLegible,
          Cantidad: cantidad
        })
      });

      const texto = await resp.text();
      if (!resp.ok) return null;

      let data;
      try { data = JSON.parse(texto); } catch { data = {}; }

      const total = toNum(getProp(data, 'total', 'Total'));
      if (!Number.isFinite(total)) return null;

      const clase = getProp(data, 'clase', 'Clase');
      const km = toNum(getProp(data, 'distanciaKm', 'DistanciaKm'));

      const partes = [];
      if (clase !== undefined) partes.push(`Clase ${clase}`);
      if (Number.isFinite(km) && km > 0) partes.push(`${km.toFixed(1)} km`);
      
      return {
        total,
        detalle: partes.join(' · ') || 'Cálculo ferroviario',
        fuente: 'Ferrocarril'
      };
    } catch (e) {
      console.error('Error llamando API ferroviaria:', e);
      return null;
    }
  };

  const buscarRuta = async () => {
    if (!origenId || !destinoId) {
      alert('Selecciona origen y destino');
      return;
    }
    if (origenId === destinoId) {
        setShowSameLocationError(true);
        return;
    }
    if (!carga) {
      alert('Selecciona el tipo de carga');
      return;
    }

    setCostoInfo(null);
    setFerroCostoInfo(null);

    // 1. Obtener Ruta
    const res = await fetch(`${API_BASE_URL}/api/CalcularRuta/calcular`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        OrigenId: origenId,
        DestinoId: destinoId,
        TipoCarga: mapTipoCarga(carga),
        Cantidad: deriveCantidad()
      })
    });

    const texto = await res.text();
    if (!res.ok) {
      console.error('Error al calcular ruta:', res.status, texto);
      return;
    }

    let data;
    try { data = JSON.parse(texto); } catch { return; }

    const origenObj = ubicaciones.find(u => u.id === origenId);
    const destinoObj = ubicaciones.find(u => u.id === destinoId);

    const kmOficial = Number(data.distanciaKm ?? 0);
    const hrs = Math.floor((Number(data.duracionMin) || 0) / 60);
    const mins = Math.round((Number(data.duracionMin) || 0) % 60);

    setRouteInfo({
      originLine: `📍 De ${origenObj.nombre}, ${origenObj.estado}, México`,
      destinationLine: `⚓ A ${destinoObj.nombre}, ${destinoObj.estado}, México`,
      departureLine: `🚚 Saliendo del puerto de ${origenObj.nombre}, ${origenObj.estado} a ${destinoObj.nombre}, ${destinoObj.estado}`,
      viaLine: `🛣️ Ruta: ${data.descripcion}`,
      durationLine: `🚗 Aprox. ${hrs} horas ${mins} minutos por Carretera (${kmOficial.toFixed(1)} km)`,
      distanciaKm: kmOficial
    });

    setOrigenCoords({ lat: origenObj.latitud, lng: origenObj.longitud });
    setDestinoCoords({ lat: destinoObj.latitud, lng: destinoObj.longitud });
    const encoded = data.encodedPolyline;
    setPolylinePath(encoded ? decode(encoded).map(([lat, lng]) => ({ lat, lng })) : []);

    // 2. Precios
    const costo = await llamarApiPrecio({ origenId, destinoId, distanciaKm: kmOficial });
    setCostoInfo(costo);

    // 3. Ferroviario
    const limpiar = (n) => limpiarNombrePuerto(n || '');
    const origenNombreClean = limpiar(origenObj?.nombre);
    const destinoNombreClean = limpiar(destinoObj?.nombre);

    try {
      const urlFerro = `${API_BASE_URL}/api/CalcularRuta/ferro?origenNombre=${encodeURIComponent(origenNombreClean)}&destinoNombre=${encodeURIComponent(destinoNombreClean)}`;
      const rF = await fetch(urlFerro);
      if (rF.ok) {
        const fer = await rF.json();
        setFerroPath((fer.estaciones || []).map(p => ({ lat: p.lat, lng: p.lng })));
        setFerroInfo({
          nombre: fer.nombre,
          rectaKm: fer.distanciaRectaKm,
          estacionesKm: fer.distanciaPorEstacionesKm
        });
      } else {
        setFerroPath([]);
        setFerroInfo(null);
      }
    } catch (e) {
      setFerroPath([]);
      setFerroInfo(null);
    }

    const ferroCost = await llamarApiPrecioFerro({ origenId, destinoId });
    setFerroCostoInfo(ferroCost);
  };

  /**
   * FUNCIÓN NUEVA: Selecciona el transporte y envía los datos a /calculo
   */
  const seleccionarTransporte = (tipo, dataCosto) => {
    navigate('/calculo', {
      state: {
        transporte: {
          tipo: tipo,
          total: dataCosto.total,
          detalle: dataCosto.detalle,
          origen: routeInfo?.originLine,
          destino: routeInfo?.destinationLine
        }
      }
    });
  };

  return (
    <div className="cotizar-container">
      <section className="filtros-card">
        <div className="barra-seleccion">
          <select value={origenId} onChange={e => setOrigenId(e.target.value)} className="select-input">
            <option value="">Origen</option>
            {ubicaciones.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
          <select value={destinoId} onChange={e => setDestinoId(e.target.value)} className="select-input">
            <option value="">Destino</option>
            {ubicaciones.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
          <select value={carga} onChange={e => setCarga(e.target.value)} className="select-input">
            <option value="">Carga</option>
            <option value="conten">Carga contenerizada</option>
            <option value="agricola">Graneles agrícolas</option>
            <option value="mineral">Graneles minerales</option>
            <option value="general">Carga general</option>
            <option value="petroleo">Petróleo y sus derivados</option>
            <option value="fluidos">Fluidos</option>
            <option value="vehiculos">Vehículos</option>
            <option value="otros">Otros</option>
          </select>
          <button onClick={buscarRuta} className="boton-buscar" title="Buscar ruta">🔍</button>
        </div>

        {carga && (
          <div className="extras-carga show">
            {carga === 'conten' && (
              <div className="field-grid">
                <div>
                  <label>Tamaño del contenedor</label>
                  <select value={contenTam} onChange={e => setContenTam(e.target.value)} className="select-input">
                    <option value="20">20 pies</option>
                    <option value="40">40 pies</option>
                  </select>
                </div>
                <div>
                  <label>Condición</label>
                  <select value={contenTipo} onChange={e => setContenTipo(e.target.value)} className="select-input">
                    <option value="full">Lleno (FCL)</option>
                    <option value="empty">Vacío</option>
                  </select>
                </div>
              </div>
            )}
            {carga === 'vehiculos' && (
              <div className="field-row">
                <label>Vehículos (unidades)</label>
                <input type="number" min="1" step="1" value={vehiculosCantidad} onChange={e => setVehiculosCantidad(e.target.value)} className="text-input" placeholder="Ej. 3" />
              </div>
            )}
            {carga === 'petroleo' && (
              <div className="field-row">
                <label>Volumen de petróleo (L)</label>
                <input type="number" min="0" step="0.01" value={petroleoLitros} onChange={e => setPetroleoLitros(e.target.value)} className="text-input" placeholder="Ej. 25000" />
              </div>
            )}
            {carga === 'fluidos' && (
              <div className="field-row">
                <label>Volumen (L)</label>
                <input type="number" min="0" step="0.01" value={fluidosLitros} onChange={e => setFluidosLitros(e.target.value)} className="text-input" placeholder="Ej. 12000" />
              </div>
            )}
            {carga === 'agricola' && (
              <div className="field-row">
                <label>Peso (toneladas)</label>
                <input type="number" min="0" step="0.01" value={granelAgriTon} onChange={e => setGranelAgriTon(e.target.value)} className="text-input" placeholder="Ej. 18.5" />
              </div>
            )}
            {carga === 'mineral' && (
              <div className="field-row">
                <label>Peso (toneladas)</label>
                <input type="number" min="0" step="0.01" value={granelMinTon} onChange={e => setGranelMinTon(e.target.value)} className="text-input" placeholder="Ej. 28.0" />
              </div>
            )}
            {carga === 'general' && (
              <div className="field-row">
                <label>Peso (toneladas)</label>
                <input type="number" min="0" step="0.01" value={generalTon} onChange={e => setGeneralTon(e.target.value)} className="text-input" placeholder="Ej. 10.0" />
              </div>
            )}
            {carga === 'otros' && (
              <div className="field-grid">
                <div>
                  <label>Unidad</label>
                  <input type="text" value={otrosUnidad} onChange={e => setOtrosUnidad(e.target.value)} className="text-input" placeholder="Ej. bultos, pallets" />
                </div>
                <div>
                  <label>Cantidad</label>
                  <input type="number" min="0" step="0.01" value={otrosCantidad} onChange={e => setOtrosCantidad(e.target.value)} className="text-input" placeholder="Ej. 50" />
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <Modal
        open={showSameLocationError}
        onClose={() => setShowSameLocationError(false)}
        titleId="error-mismo-destino"
      >
        <h2 id="error-mismo-destino" style={{ color: '#D32F2F', marginTop: 0 }}>Ubicación inválida</h2>
        <p>El puerto de origen y el de destino no pueden ser el mismo.</p>
        <div className="modal-actions">
          <button onClick={() => setShowSameLocationError(false)}>Entendido</button>
        </div>
      </Modal>

      <Modal
        open={!!routeInfo}
        onClose={() => { setRouteInfo(null); setCostoInfo(null); setFerroCostoInfo(null); }}
        titleId="ruta-exito-title"
      >
        <h2 id="ruta-exito-title">Ruta trazada con éxito</h2>

        {isTranslated && (
           <div style={{fontSize: '0.8rem', color: '#888', marginBottom: '10px', textAlign: 'right'}}>
               Tipo de cambio aplicado: 
               <span style={{ fontWeight: 'bold', marginLeft: '4px', color: '#000' }}>
                   ${TIPO_CAMBIO.toFixed(4)}
               </span> 
               MXN/USD
           </div>
        )}

        <p>{routeInfo?.originLine}</p>
        <p>{routeInfo?.destinationLine}</p>
        <p>{routeInfo?.departureLine}</p>
        <p>{routeInfo?.viaLine}</p>
        <p>{routeInfo?.durationLine}</p>

        {costoInfo && (
          <div style={{ marginTop: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', background: '#f9f9f9' }}>
            <p style={{ margin: '0 0 5px 0' }}>🚚 <strong>Costo estimado por Carretera:</strong> 
               <span style={{ color: '#000', fontWeight: 'bold', marginLeft: '5px' }}>
                 {formatCurrency(costoInfo.total)}
               </span>
            </p>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#666' }}>🔎 <small>{costoInfo.detalle}</small></p>
            
            <button 
                onClick={() => seleccionarTransporte('Carretera', costoInfo)}
                style={{ 
                    padding: '8px 12px', 
                    background: '#003366', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold'
                }}
            >
                Seleccionar Carretera y Cotizar Servicios
            </button>
          </div>
        )}

        {ferroCostoInfo && (
          <div style={{ marginTop: '15px', padding: '10px', border: '1px solid #b0bec5', borderRadius: '6px', background: '#f0f4f8' }}>
            <p style={{ margin: '0 0 5px 0' }}>🚆 <strong>Costo estimado por Ferrocarril:</strong> 
               <span style={{ color: '#000', fontWeight: 'bold', marginLeft: '5px' }}>
                 {formatCurrency(ferroCostoInfo.total)}
               </span>
            </p>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#666' }}>🔎 <small>{ferroCostoInfo.detalle}</small></p>

            <button 
                onClick={() => seleccionarTransporte('Ferrocarril', ferroCostoInfo)}
                style={{ 
                    padding: '8px 12px', 
                    background: '#003366', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold'
                }}
            >
                Seleccionar Ferrocarril y Cotizar Servicios
            </button>
          </div>
        )}

        {ferroInfo && !ferroCostoInfo && (
           <div style={{ marginTop: '15px' }}>
             <hr />
             <p>🧭 <strong>{ferroInfo.nombre}</strong></p>
             <p>🔹 Distancia Ferroviaria: <strong>{ferroInfo.estacionesKm} km</strong></p>
             <p><small>Tarifa no disponible para cotización automática.</small></p>
           </div>
        )}

        <div className="modal-actions">
          <button onClick={() => { setRouteInfo(null); setCostoInfo(null); setFerroCostoInfo(null); }}>Cerrar</button>
        </div>
      </Modal>

      <LoadScript googleMapsApiKey={apiKey}>
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={6}>
          {origenCoords && <Marker position={origenCoords} label="Origen" />}
          {destinoCoords && <Marker position={destinoCoords} label="Destino" />}
          {polylinePath.length > 0 && (
            <Polyline path={polylinePath} options={{ strokeColor: '#FF0000', strokeOpacity: 0.8, strokeWeight: 5 }} />
          )}
          {ferroPath.length > 0 && (
            <>
              <Polyline path={ferroPath} options={railBaseOptions} />
              <Polyline path={ferroPath} options={railSleepersOptions} />
            </>
          )}
          {ferroPath.map((p, i) => <Marker key={`ferro-${i}`} position={p} label={`${i + 1}`} />)}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default Cotizar;